import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, apiUpload } from '@/lib/http';

export type TaxRegime = 'SIMPLES_NACIONAL' | 'LUCRO_PRESUMIDO' | 'LUCRO_REAL';

export const REGIME_LABELS: Record<TaxRegime, string> = {
  SIMPLES_NACIONAL: 'Simples Nacional',
  LUCRO_PRESUMIDO: 'Lucro Presumido',
  LUCRO_REAL: 'Lucro Real',
};

export interface FiscalProfile {
  regime: TaxRegime;
  simplesAnnex: 'I' | 'II' | null;
  rateMode: 'MANUAL' | 'CALCULATED';
  manualRate: string | null;
  validFrom: string;
}

export type CurrentTaxRate =
  | {
      status: 'AVAILABLE';
      rate: string;
      source: 'MANUAL' | 'CALCULATED';
      competence: string;
      detail?: { rbt12: string; band: number; tableVersion: string };
      fallbackReason?: string;
      /** A última calculada, repetida porque falta o faturamento recente. */
      carriedReason?: string;
    }
  | { status: 'MISSING'; reason: string };

export interface RevenueMonth {
  competence: string;
  grossRevenue: string;
  source: 'MANUAL' | 'IMPORT';
}

export interface EffectiveRateRow {
  competence: string;
  rbt12: string;
  band: number;
  nominalRate: string;
  deduction: string;
  effectiveRate: string;
  tableVersion: string;
}

export const fiscalKeys = {
  all: ['fiscal'] as const,
  profile: () => [...fiscalKeys.all, 'profile'] as const,
  revenue: () => [...fiscalKeys.all, 'revenue'] as const,
  rates: () => [...fiscalKeys.all, 'rates'] as const,
};

export function useFiscalProfile() {
  return useQuery({
    queryKey: fiscalKeys.profile(),
    queryFn: () =>
      apiRequest<{ profile: FiscalProfile | null; currentRate: CurrentTaxRate }>('/fiscal/profile'),
  });
}

export function useRevenue() {
  return useQuery({
    queryKey: fiscalKeys.revenue(),
    queryFn: () => apiRequest<RevenueMonth[]>('/fiscal/revenue'),
  });
}

export function useRates() {
  return useQuery({
    queryKey: fiscalKeys.rates(),
    queryFn: () => apiRequest<EffectiveRateRow[]>('/fiscal/rates'),
  });
}

function useInvalidateFiscal() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: fiscalKeys.all });
}

export function useUpdateProfile() {
  const invalidate = useInvalidateFiscal();
  return useMutation({
    mutationFn: (body: Omit<FiscalProfile, 'validFrom'>) =>
      apiRequest<void>('/fiscal/profile', { method: 'PUT', body }),
    onSuccess: invalidate,
  });
}

export function useImportRevenue() {
  const invalidate = useInvalidateFiscal();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return apiUpload<{ imported: number }>('/fiscal/revenue/import', form);
    },
    onSuccess: invalidate,
  });
}

export async function downloadRevenueTemplate(): Promise<void> {
  const blob = await apiRequest<Blob>('/fiscal/revenue/template.xlsx', { parse: 'blob' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'faturamento-12-meses.xlsx';
  link.click();
  URL.revokeObjectURL(url);
}
